import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { LambdaIamPoliciesEditor } from './LambdaIamPoliciesEditor';
import { IamPolicyMultiSelect } from '../../IamPolicyMultiSelect';
import IamIcon from '../../../assets/aws/iam.svg';

const useStyles = makeStyles(theme => ({
  iamSection: {
    marginTop: theme.spacing(3),
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    minHeight: 48,
    '&.Mui-expanded': {
      minHeight: 48,
    },
  },
  accordionDetails: {
    flexDirection: 'column',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  icon: {
    width: '1.25rem',
    height: '1.25rem',
    // SVG will use its own colors
  },
  sectionDescription: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  fieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  iamPoliciesSection: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
}));

interface LambdaIamSectionProps {
  /**
   * Standard IAM policy names
   */
  iamPolicyNames?: string[];
  /**
   * Custom IAM policy names
   */
  customIamPolicyNames?: string[];
  /**
   * Callback when IAM policy names change
   */
  onIamPolicyNamesChange: (policies: string[]) => void;
  /**
   * Callback when custom IAM policy names change
   */
  onCustomIamPolicyNamesChange: (policies: string[]) => void;
  /**
   * Description text for the section
   */
  description?: string;
}

/**
 * Dedicated IAM section component that groups all IAM-related fields.
 * Can be used for both individual lambdas and defaults.
 */
export const LambdaIamSection = ({
  iamPolicyNames = [],
  customIamPolicyNames = [],
  onIamPolicyNamesChange,
  onCustomIamPolicyNamesChange,
  description = 'Configure IAM policies and permissions',
}: LambdaIamSectionProps) => {
  const classes = useStyles();

  return (
    <Accordion className={classes.iamSection} defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
        <div className={classes.titleContainer}>
          <img src={IamIcon} alt="IAM" className={classes.icon} />
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            IAM Configuration
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <Typography variant="body2" className={classes.sectionDescription}>
          {description}
        </Typography>

        <div className={classes.fieldsContainer}>
          <LambdaIamPoliciesEditor
            iamPolicyNames={iamPolicyNames}
            onUpdate={onIamPolicyNamesChange}
            title="Standard IAM Policies"
          />

          <div className={classes.iamPoliciesSection}>
            <div style={{ marginBottom: 16 }}>
              <Typography variant="subtitle2">Custom IAM Policy Names</Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ fontSize: '0.75rem', marginTop: 4 }}
              >
                Enter custom IAM policy names that are not available in the standard policies
              </Typography>
            </div>

            <IamPolicyMultiSelect
              value={customIamPolicyNames}
              onChange={onCustomIamPolicyNamesChange}
              label="Custom IAM Policy Names"
              helperText="Enter custom IAM policy names"
              variant="outlined"
              size="small"
            />
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
